<template>
    <div class="student-search">
        <page-image-top class="search-top"
                        :title="$t(`pages.students.title`)"
                        image="/static/backgrounds/table-top.jpeg">
            <div class="filters box is-radiusless is-borderless">
                <div class="container">
                    <div class="columns level">
                        <div class="column is-narrow level-item">
                            <h1 class="subtitle">
                                Filtrering

                                <b-tag class="reset-link is-primary is-hidden-tablet"
                                       rounded
                                       @click.native="resetFilters">
                                    Nulstil
                                </b-tag>
                            </h1>
                        </div>

                        <div class="column is-narrow level-item">
                            <b-field>
                                <b-select v-model="selectedSkill"
                                          placeholder="Vælg en færdighed"
                                          :loading="!skillGroups.length"
                                          icon="clipboard-text"
                                          expanded>
                                    <option :value="null">Alle</option>

                                    <optgroup v-for="skillGroup in skillGroups"
                                              :key="skillGroup.id"
                                              :label="$t('skills.groups.' + skillGroup.name + '.name')">
                                        <option v-for="skill in skillGroup.skills"
                                                :value="skill"
                                                :key="skill.id">
                                            {{ $t('skills.' + skill.name + '.name') }}
                                        </option>
                                    </optgroup>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column is-narrow level-item">
                            <StudentNameSearch :students="allStudents"/>

                        </div>

                        <div class="column level-right is-hidden-mobile">
                            <span class="level-item reset-link is-pulled-right is-primary">
                                <small>Nulstil</small>
                                &nbsp;
                                <b-icon @click.native="resetFilters"
                                        icon="close-circle-outline" />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </page-image-top>

        <div class="container students">
            <div v-if="students.length"
                 class="columns is-multiline">
                <div v-for="student in students"
                     :key="student.id"
                     class="column is-full-mobile is-half-tablet is-one-third-widescreen">
                    <student class="is-shadowless"
                             :student="student"
                             :displaySkill="selectedSkill" />
                </div>
            </div>

            <div class="card"
                 v-else-if="hasLoaded">
                <div class="card-content">
                    <h3>Det er ikke nogen studerende med de valgte filtre.</h3>
                </div>
            </div>

            <loader v-else
                    text="Indlæser.."
                    style="min-height: 300px" />
        </div>

        <div class="pagination-bar box is-radiusless"
             v-if="total > $options.limit">
            <div class="container">
                <b-pagination :total="total"
                              :current.sync="page"
                              :per-page="$options.limit"
                              order="is-centered" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '../../../global/styles/variables';

.student-search {
    background: $background-blueish;

    .search-top {
        margin-bottom: 1.75rem;

        .filters {
            .reset-link {
                user-select: none;
                cursor: pointer;

                &:hover {
                    background-color: $primary-invert;
                    color: $primary;
                }
            }
        }
    }

    .students {
        padding-bottom: 1.75rem;
    }

    .pagination-bar {
        background: $white-ter;
        margin-bottom: 1px;

        padding: 0.75rem;
    }
}

// TODO: Consider using variable for media query
@media screen and (min-width: 769px) {
    .student-search .search-top .filters {
        padding: 0 10px;

        .subtitle {
            padding-left: 10px;
        }
    }
}
</style>

<script>
import Loader from '@/global/components/Loader';
import PageImageTop from '@/global/components/PageImageTop';
import StudentService from '@/taskflex/students/services/StudentService';
import SkillGroupService from '@/taskflex/skills/services/SkillGroupService';
import SkillService from '@/taskflex/skills/services/SkillService';
import Student from '@/taskflex/students/components/search/Student';
import StudentNameSearch from '../components/search/StudentNameSearch.vue';

import { mapGetters, mapMutations } from 'vuex';

export default {
    data() {
        return {
            allStudents: [],
            students: [],
            skillGroups: [],
            page: 1,
            total: 0,
            hasLoaded: false,
            selectedSkill: null,
        };
    },

    limit: 9,

    computed: {
        ...mapGetters('students', [
            'getSelectedSkill',
        ]),
    },

    watch: {
        page() {
            this.loadStudents();
        },

        selectedSkill(skill) {
            this.loadStudents();
            this.setSelectedSkill(skill);
        },
    },

    mounted() {
        this.checkForSelectedSkill();

        this.loadStudents();
        this.loadSkillGroups();
    },

    methods: {
        ...mapMutations('students', [
            'setSelectedSkill',
        ]),

        resetFilters() {
            this.selectedSkill = null;
        },

        checkForSelectedSkill() {
            const {
                $route: {
                    params: {
                        skill: routeSkillName = null,
                    },
                },
                getSelectedSkill: storedSkill = null,
            } = this;

            const skillName = routeSkillName;

            if (!skillName && storedSkill) {
                this.selectedSkill = storedSkill;
                return;
            }

            if (!skillName) {
                return;
            }

            SkillService.findByName(skillName, {}, ({ data }) => {
                this.selectedSkill = data;
            });
        },

        loadStudents() {
            this.students = [];
            this.hasLoaded = false;

            const { selectedSkill } = this;

            // Default filters
            const filterGroup = [
                {
                    key: 'activated',
                    operator: 'eq',
                    value: 1,
                },
                {
                    key: 'is_private',
                    operator: 'neq',
                    value: 0,
                },
            ];

            // Filter by skill
            if (selectedSkill) {
                filterGroup.push({
                    key: 'hasSkill',
                    operator: 'eq',
                    value: selectedSkill.id,
                });
            }

            const options = {
                includes: ['skills', 'level', 'skills.paygrades', 'ratings'],

                limit: this.$options.limit,
                page: this.page,

                filter_groups: [
                    {
                        filters: filterGroup,
                    },
                ],
            };

            // Load all students into name search component
            const modifiedOptions = { ...options, limit: 9999 };
            StudentService.findAll(modifiedOptions, ({ data: { rows } }) => {
                this.allStudents = rows;
            });

            StudentService.findAll(options, ({ data: { rows, total } }) => {
                this.students = rows;
                this.total = total;

                this.hasLoaded = true;
            });
        },

        loadSkillGroups() {
            const options = {
                includes: ['skills'],

                filter_groups: [
                    {
                        filters: [
                            {
                                key: 'hasSkillsWithStudents',
                                operator: 'eq',
                                value: 1,
                            },
                        ],
                    },
                ],
            };

            SkillGroupService.findAll(options, ({ data }) => {
                this.skillGroups = data.rows;
            });
        },
    },

    components: {
        Loader,
        PageImageTop,
        Student,
        StudentNameSearch,
    },
};
</script>



// WEBPACK FOOTER //
// src/taskflex/students/pages/Search.vue